import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Adds (or updates) the single <link rel="canonical"> tag for the current page.
 *
 * Important: we DO NOT remove the tag on unmount. Removing it causes a brief
 * window where Googlebot may see no canonical at all, or the next page's
 * hook adds a second tag before the cleanup of the previous one runs.
 * Instead we always reuse the existing tag and overwrite its href, so there
 * is exactly one canonical at any given moment.
 *
 * Note: pages that use <Helmet><link rel="canonical" .../></Helmet> should
 * NOT also call this hook — Helmet manages its own canonical element.
 */
export const useCanonicalTag = (customPath?: string) => {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = window.location.origin;
    const canonicalPath = customPath || location.pathname;
    const canonicalUrl = `${baseUrl}${canonicalPath}`;

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }

    canonicalLink.setAttribute('href', canonicalUrl);
    // No cleanup: leave the tag in place so the next route can overwrite it
    // without a flicker of "no canonical".
  }, [location.pathname, customPath]);
};
